import { FC, useState } from 'react';
import { useCreate, useNavigation, useTranslate, useNotification } from '@refinedev/core';
import { useForm } from '@refinedev/react-hook-form';
import { FormBuilder } from '~/components';
import { resourceNames } from '~/resources/resource-names';
import { Asset, CreateChallengeInput, getSdk } from '~/api';
import { GQLClient } from '~/config/gql-client';
import { challengeFormBuilder } from '../form';
import { ChallengeLinksForm, validateChallengeLinks } from '~/components/ChallengeLinksForm';

const client = GQLClient.getInstance();

export const ChallengeCreatePage: FC = () => {
  // ======================= HOOKS
  const navigation = useNavigation();
  const t = useTranslate();
  const notif = useNotification();
  const [handlingAsset, setHandlingAsset] = useState(false);

  // ======================= API
  const { mutate: createChallenge, isLoading: creating } = useCreate({
    mutationOptions: { onSettled: () => setHandlingAsset(false) },
  });

  // ======================= EVENTS
  const onCreate = async (data: any) => {
    // Extract form data
    const {
      title,
      description,
      startDate,
      endDate,
      enabled,
      type,
      limitPerCustomer,
      asset,
      links,
      outletId,
    } = data;

    // Validate challenge links
    if (!validateChallengeLinks(links)) {
      notif?.open?.({
        message: t('challenges.errors.linksRequired', { 
          fallback: 'At least one challenge link with URL and platform is required' 
        }),
        type: 'error',
      });
      return;
    }

    // Handle asset upload if provided
    let assetId;
    if (asset?.length) {
      setHandlingAsset(true);
      try {
        const assetRes = await getSdk(client).createAssets({ input: [{ file: asset[0] }] });
        const assetsData = assetRes?.createAssets as Asset[];

        if (!assetsData?.[0]?.id) {
          console.warn(`[Failed to get asset]`, assetsData);
          notif?.open?.({
            message: t('common.somethingWrong', {
              fallback: 'Something went wrong, Please try again',
            }),
            type: 'error',
          });
          setHandlingAsset(false);
          return;
        }
        assetId = assetsData[0].id;
      } catch (error) {
        console.error('[Asset upload error]', error);
        notif?.open?.({
          message: t('common.somethingWrong', { fallback: 'Failed to upload image' }),
          type: 'error',
        });
        setHandlingAsset(false);
        return;
      }
    }

    // Process links - they come directly as array from ChallengeLinksForm
    const processedLinks = [];
    if (links && Array.isArray(links)) {
      for (const link of links) {
        // Only add links with URL and platformId
        if (link.url && link.platformId) {
          processedLinks.push({
            platformId: link.platformId,
            url: link.url,
            title: link.title || undefined,
            description: link.description || undefined,
          });
        }
      }
    }

    // Prepare challenge input
    const challengeInput: any = {
      title,
      description,
      startDate: new Date(startDate).toISOString(),
      endDate: endDate ? new Date(endDate).toISOString() : undefined,
      enabled: enabled ?? true,
      type,
      limitPerCustomer: limitPerCustomer !== undefined && limitPerCustomer !== '' && limitPerCustomer !== null ? Number(limitPerCustomer) : null,
      assetId,
      outletId,
    };

    // Add links to the input if we have any
    if (processedLinks.length > 0) {
      challengeInput.links = processedLinks;
    }

    // Create challenge
    createChallenge(
      {
        resource: resourceNames.challenge,
        values: challengeInput,
        errorNotification: {
          message: t('challenges.create.failed', { fallback: 'Failed to create challenge' }),
          type: 'error',
        },
        successNotification: {
          message: t('challenges.create.success', { fallback: 'Challenge created successfully' }),
          type: 'success',
        },
      },
      {
        onSuccess: (data) => {
          const challengeId = data.data.id;
          if (challengeId) {
            navigation.show(resourceNames.challenge, challengeId);
          } else {
            navigation.list(resourceNames.challenge);
          }
        },
      }
    );
  };

  return (
    <FormBuilder
      resourceName={resourceNames.challenge}
      title={t('challenges.create.name', { fallback: 'Create Challenge' })}
      loading={creating || handlingAsset}
      onSubmit={onCreate}
      items={(formHook) => {
        const fields = challengeFormBuilder(undefined, formHook);

        return fields;
      }}
      extra={[
        {
          key: 'links',
          render: ({ setValue, watch }) => {
            const links = watch('links') || [];
            return (
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  {t('challenges.form.links', { fallback: 'Challenge Links' })}
                </h3>
                <ChallengeLinksForm 
                  value={links} 
                  onChange={(newLinks) => setValue('links', newLinks)} 
                />
              </div>
            );
          },
        },
      ]}
    />
  );
};
